import React from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { NavLink } from "react-router-dom";

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { regular, solid } from '@fortawesome/fontawesome-svg-core/import.macro';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import { styled, alpha } from '@mui/material/styles';

import { ReactComponent as Logo } from '../../assets/images/global/marsh-logo-white.svg';
import { ReactComponent as LangIcon } from '../../assets/images/global/lang_icon.svg';
import './Header.styles.css';
import i18next from 'i18next';
import { useDispatch, useSelector } from 'react-redux';
import { logout } from '../../redux/actions/user.actions';
import { adjustFontSize, changeLang } from '../../redux/actions/appSettings.actions';
import { getInitials, sanitiseInput } from '../../utils/helperFn';
import useWindowSize from '../../hooks/useWindowSize';
import { getApiUrl } from '../../utils/apiUrls';
import instance from '../../utils/axios';
import { apiErrorHandler } from '../../utils/errorHandler';
import { openSnackBar } from '../../redux/actions/snackbar.actions';
import { useTranslation } from 'react-i18next';
import { translationLanguageKey } from "../../assets/data/allowedParameters"

const textResizeOptions = [
    {
        label: 'A+',
        value: 'large' 
    },
    {
        label: 'A',
        value: 'normal' 
    },
    {
        label: 'A-',
        value: 'small' 
    },
]

const StyledMenu = styled((props) => (
    <Menu
      elevation={0}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'right',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      autoFocus={false}
      {...props}
    />
  ))(({ theme }) => ({
    '& .MuiPaper-root': {
      borderRadius: 6,
      marginTop: theme.spacing(1),
      minWidth: 180,
      color:
        theme.palette.mode === 'light' ? 'rgb(55, 65, 81)' : theme.palette.grey[300],
      boxShadow:
        'rgb(255, 255, 255) 0px 0px 0px 0px, rgba(0, 0, 0, 0.05) 0px 0px 0px 1px, rgba(0, 0, 0, 0.1) 0px 10px 15px -3px, rgba(0, 0, 0, 0.05) 0px 4px 6px -2px',
      '& .MuiMenu-list': {
        padding: '4px 0',
      },
      '& .MuiMenuItem-root': {
        '& .MuiSvgIcon-root': {
          fontSize: 18,
          color: theme.palette.text.secondary,
          marginRight: theme.spacing(1.5),
        },
        '&:active': {
          backgroundColor: alpha(
            theme.palette.primary.main,
            theme.palette.action.selectedOpacity,
          ),
        },
      },
    },
  }));

const Header = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const user = useSelector(state => state.user);
    const location = useLocation();
    const appSettings = useSelector(state => state.appSettings);
    const clientData = useSelector(state => state.clientData);
    const textResizeState = useSelector(state => state.appSettings.textSize);
    const windowSize = useWindowSize();
    const [searchParams] = useSearchParams();
    const getLang = searchParams.get('lang');
    const getRefId = searchParams.get('refId');
    const eventId = searchParams.get('eventId');

    const handleLogout = () => {
        dispatch(logout(appSettings.lang, getRefId));
        handleCloseUserMenu();
    }

    const [anchorElUser, setAnchorElUser] = React.useState(null);
    const [anchorElResizer, setAnchorElResizer] = React.useState({
        el: null,
        active: 'normal'
    });
    const [anchorElLang, setAnchorElLang] = React.useState({
        el: null,
        lang: null,
        allLangs: null,
        isLoading: false,
    });

    const handleCloseUserMenu = () => {
      setAnchorElUser(null);
    };

    const pages = React.useMemo(
      () => [
        {
          label: t("view_profile"),
          iconClass: regular("user"),
          clickFn: () => {
            navigate(`/profile${getRefId ? `?refId=${getRefId}` : ""}`);
            handleCloseUserMenu();
          },
        },
        {
          label: t("view_report"),
          iconClass: regular("file-alt"),
          clickFn: () => {
            handleCloseUserMenu();
            navigate(
              `/candidate-report?reportType=EVENT${
                getRefId ? `&refId=${getRefId}` : ""
              }${eventId ? `&eventId=${eventId}` : ""}`
            );
          },
        },
        {
          label: t("logout"),
          iconClass: regular("right-from-bracket"),
          clickFn: handleLogout,
        },
      ],
      [t, handleLogout, handleCloseUserMenu, getRefId]
    );

    React.useEffect(() => {
        if(!!getLang && !!anchorElLang?.allLangs) {
            const langExists = anchorElLang?.allLangs.find(el => el.languageCode === getLang);

            if(!langExists) {
                const safePath = sanitiseInput(location.pathname);
                
                // Validate it matches expected path pattern (alphanumeric, -, /)
                const pathPattern = /^\/[a-zA-Z0-9\-\/]*$/;
                const isValidPath = pathPattern.test(safePath);
                
                if (isValidPath) {
                    window.location.replace(safePath + "?lang=en");
                } else {
                    window.location.replace("/?lang=en");
                }
            }
        }
    }, [anchorElLang, getLang])

    React.useEffect(() => {
        fetchAllLangs()
    }, []);

    const fetchAllLangs = async (element) => {
        try {
            setAnchorElLang({ ...anchorElLang, isLoading: true })
            const res = await instance.get(`${getApiUrl("getAllLanguages")}`);
            
            if(!!res.data) {
                const langsToSet = res.data.map(el => {
                    if(!!el?.name) {
                        el.label = el.name?.toLowerCase();
                        el.actualName = el.name === "MANDARIN" ? "CHINESE_SIMPLIFIED" : el.name;
                    }
                    if(!!el?.languageCode) el.value = el.languageCode;
                    return el;
                }).sort((a, b) => a.actualName.localeCompare(b.actualName));

                const selectedLang = !!appSettings?.lang ? langsToSet.find(el => el.value === appSettings?.lang) : langsToSet[0];

                setAnchorElLang({
                    ...anchorElLang,
                    lang: selectedLang,
                    // el: element,
                    isLoading: false,
                    allLangs: langsToSet
                })
            }

        } catch (error) {
            setAnchorElLang({ ...anchorElLang, isLoading: false })
            const msgObj = apiErrorHandler(error);
            dispatch(
                openSnackBar({
                    msg: t(msgObj.statusText),
                    type: "error",
                })
            );
        }
    }

    const handleOpenLang = (event) => {
        // if(!anchorElLang.isLoading) fetchAllLangs(event.currentTarget)
        setAnchorElLang({
            ...anchorElLang,
            el: event.currentTarget
        });
    };

    const saveSelectedLanguage = async (langCode) => {
        try {
            if(!translationLanguageKey.includes(langCode)) {
               throw Error("generic_error")
            }
            const res = await instance.post(`${getApiUrl("saveLanguage")}/${langCode}`)

            if(res.data.statusCode === "SUCCESS") {
                return true;
            } else {
                return false
            }

        } catch (error) {
            const msgObj = apiErrorHandler(error);
            dispatch(
                openSnackBar({
                    msg: t(msgObj.statusText),
                    type: "error",
                })
            );

            return false;
        }
    }

    const handleLanguageSelect = async (data) => {
        let response = null;

        if(!!user) {
            response = await saveSelectedLanguage(data.value);
        } else {
            response = true;
            // Sanitize and validate the pathname before using it
            const safePath = sanitiseInput(location.pathname);
            const pathPattern = /^\/[a-zA-Z0-9\-\/]*$/;
            const isValidPath = pathPattern.test(safePath);
            
            if (isValidPath) {
                navigate(`${safePath}?lang=${data.value}`);
            } else {
                navigate(`/?lang=${data.value}`);
            }
        }

        if(!!response) {
            i18next.changeLanguage(data.value);
            // Manually reload translations for new language
            i18next.reloadResources([data.value]);

            setAnchorElLang({
                ...anchorElLang,
                lang: data
            });
            dispatch(changeLang(data.value))
            handleCloseLang();
        }
    }

    const handleCloseLang = () => {
        setAnchorElLang({
            ...anchorElLang,
            el: null
        });
    };

    //Add class to body for Text Resizing
    React.useEffect(() => {
        if(textResizeState === 'large') {
            document.body.classList.add('txt-size-lg');
            document.body.classList.remove('txt-size-sm');
        } else if(textResizeState === 'small') {
            document.body.classList.add('txt-size-sm');
            document.body.classList.remove('txt-size-lg');
        } else {
            document.body.classList.remove('txt-size-sm');
            document.body.classList.remove('txt-size-lg');
        }

    }, [textResizeState])

    const handleOpenUserMenu = (event) => {
        setAnchorElUser(event.currentTarget);
    };

    const handleOpenResizer = (event) => {
        setAnchorElResizer({
            ...anchorElResizer,
            el: event.currentTarget
        });
    };

    const handleCloseResizer = () => {
        setAnchorElResizer({
            ...anchorElResizer,
            el: null
        });
    };

    const loadTextResizeOptions = () => {
        return textResizeOptions.map(el => (
        <li className={`text-center cm-pointer ${el.value === textResizeState ? 'bg-action-1 text-white border-action-1' : 'text-blue-800'}`} key={el.value} onClick={() => dispatch(adjustFontSize(el.value))}>
            <span>{el.label}</span>
        </li>))
    }

    return(
        <header className={`cm-header-container bg-primary d-flex justify-content-between align-items-center py-3 px-4 ${user !== null ? 'cm-user-header' : '' }`}>
            <div className="cm-header-logo px-3 d-flex align-items-center">
                <Link to={`/${getRefId ? `?refId=${getRefId}` : ''}`}>
                    {clientData?.logo ? <img src={clientData?.logo} style={{ maxHeight: "32px" }} className="w-100" alt="Client Logo" /> : <Logo height={24} width={124} className='d-block' />}
                </Link>
                {
                    user ? 
                    <>
                        <div className='cm-header-logo-separator bg-blue-gray-700 my-0 ms-4 d-block' />
                        <ul className="navbar-nav me-auto">
                            <li>
                                <NavLink
                                    to={`/${getRefId ? `?refId=${getRefId}` : ''}`}
                                    style={({ isActive }) =>
                                        isActive ? { backgroundColor: "var(--bs-blue-800)", color: "var(--bs-white)"} : {color: "var(--bs-white)"}
                                    }
                                    className="py-2 px-3 cm-sm-txt fw-semibold cm-btn-radius ms-4"
                                >
                                    {t('home')}
                                </NavLink>
                            </li>
                        </ul>
                    </>
                    : null
                }
            </div>
            <div className="cm-header-op text-white d-flex align-items-center px-3">
                <span className={`cm-txt-resizer ${Boolean(anchorElLang.el) ? 'bg-blue-800' : ''} d-flex align-items-center justify-content-center cm-pointer`} onClick={handleOpenLang}>
                    <LangIcon />
                </span>
                <StyledMenu
                    id="language-menu"
                    MenuListProps={{
                        'aria-labelledby': 'demo-customized-button',
                        "className": "cm-select-modal"
                    }}
                    anchorEl={anchorElLang.el}
                    open={Boolean(anchorElLang.el)}
                    onClose={handleCloseLang}
                    className="cm-action-menu"
                >
                    {anchorElLang.allLangs?.map(el => (
                        <MenuItem key={el.value} onClick={() => {
                            if(el.value === appSettings.lang) {
                                handleCloseLang();
                            } else {
                                handleLanguageSelect(el)
                            }
                        }} disableRipple className={`text-capitalize cm-sm-txt text-blue-800 ${el.value === appSettings.lang? 'cm-active-app-menu-item' : ""}`}> 
                            {t(el.label)}
                        </MenuItem>
                    ))}
                </StyledMenu>
                <span className={`cm-txt-resizer ${Boolean(anchorElResizer.el) ? 'bg-blue-800' : ''} d-flex align-items-center justify-content-center cm-pointer`} onClick={handleOpenResizer}>
                    <FontAwesomeIcon icon={regular('font')} />
                </span>
                <Menu
                    sx={{ mt: '0', ml: '-36px' }}
                    id="txt-resizer"
                    anchorEl={anchorElResizer.el}
                    anchorOrigin={{
                        vertical: 'top',
                        horizontal: 'right',
                    }}
                    keepMounted
                    transformOrigin={{
                        vertical: 'top',
                        horizontal: 'right',
                    }}
                    open={Boolean(anchorElResizer.el)}
                    onClose={handleCloseResizer}
                    MenuListProps={{ disablePadding: true }}
                    elevation={0}
                    PaperProps={{  
                        style: {  
                            width: 174,  
                        },
                    }}
                >
                    <li><p className='my-0 cm-xs-txt text-blue-gray-700 fw-semibold text-uppercase'>{t('adjust_text_size')}</p></li>
                    {loadTextResizeOptions()}
                </Menu>
                {
                    user ?
                    <>
                        {/* <FontAwesomeIcon icon={regular('bell')} className="me-3 cm-pointer"/> */}
                    </> : null
                }
                <a href="https://support.mettl.com" target="_blank" rel='noreferrer'  className="me-3 cm-pointer text-white">
                    <FontAwesomeIcon icon={regular('circle-question')} />
                </a>
                {
                    user ?
                    <>
                        <div className='cm-header-logo-separator bg-blue-gray-700 my-0 ms-4 d-block' />

                        <div className="cm-user-avatar-wrap ms-3 cm-pointer d-flex align-items-center" onClick={handleOpenUserMenu}>
                            <div className="cm-user-avatar bg-purple rounded-circle d-flex align-items-center justify-content-center">
                                <p className='cm-xs-txt my-0 fw-semibold cm-pointer text-uppercase'>{getInitials(`${user.firstName} ${!!user.lastName ? user.lastName : ''}`)}</p>
                            </div>
                            <FontAwesomeIcon icon={solid('caret-down')} className="cm-xs-txt ms-2"/>
                        </div>
                        <Menu
                            sx={{ mt: windowSize.isMobile ? '40px' : '58px' }}
                            id="menu-appbar"
                            anchorEl={anchorElUser}
                            anchorOrigin={{
                                vertical: 'top',
                                horizontal: 'right',
                            }}
                            keepMounted
                            transformOrigin={{
                                vertical: 'top',
                                horizontal: 'right',
                            }}
                            open={Boolean(anchorElUser)}
                            onClose={handleCloseUserMenu}
                            MenuListProps={{ disablePadding: true }}
                            elevation={0}
                            PaperProps={{  
                                style: {  
                                  width: 220,  
                                },
                            }}
                        >
                            <li><p className='p-2 my-0 cm-xs-txt text-blue-gray-700 fw-semibold text-uppercase'>{t('account_actions')}</p></li>
                            {pages.map((page) => (
                                <MenuItem key={page.label} onClick={!!page.clickFn ? page.clickFn : handleCloseUserMenu}>
                                    <FontAwesomeIcon icon={page.iconClass} className="text-blue-gray-500"/>
                                    <span className='ms-3 cm-sm-txt fw-medium text-blue-800'>{page.label}</span>
                                </MenuItem>
                            ))}
                        </Menu>
                    </>
                    : null
                }
            </div>
        </header>
    )
}

export default Header;